import React, { useState } from 'react';
import { FileText, CheckCircle, Trash2, Sparkles, Upload, Link } from 'lucide-react';
import { useConfig } from '../hooks/useConfig';
import { getDirectMediaUrl } from '../utils/businessConfigHelpers';
import { supabase } from '../supabaseClient';

export default function MenuMediaPage() {
  const { 
    activeBusiness, 
    activeBusinessId,
    updateActiveBusinessField,
    saveBusinessSettings,
    showNotification
  } = useConfig();

  const [isUploadingMenu, setIsUploadingMenu] = useState(false);
  const [tempLinkUrl, setTempLinkUrl] = useState(""); // For option 2

  if (!activeBusiness) return <div>Cargando...</div>;

  const handleMenuFileUpload = async (file) => {
    if (!file) return;
    if (file.type && file.type === 'application/pdf') {
      showNotification("La Opción 1 es solo para fotos de la carta (JPG, PNG). Para archivos PDF o catálogos web, utiliza la Opción 2.", "warning");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      showNotification("La foto supera el límite máximo de 10 MB.", "error");
      return;
    }
    setIsUploadingMenu(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `menu_${activeBusinessId}_${Date.now()}.${fileExt}`;
      const filePath = `menus/${fileName}`;

      // Intentar subir a Supabase Storage CDN
      let { error: uploadError } = await supabase.storage
        .from('menu-files')
        .upload(filePath, file, { upsert: true });

      if (!uploadError) {
        const { data: { publicUrl } } = supabase.storage
          .from('menu-files')
          .getPublicUrl(filePath);

        updateActiveBusinessField('menu_pdf_url', publicUrl);
        await saveBusinessSettings({ menu_pdf_url: publicUrl });
        showNotification("¡Foto de la carta subida y guardada exitosamente en la nube!", "success");
        return;
      }

      // If Supabase fails, fallback could be base64 (omitted here for simplicity, fallback to error)
      throw uploadError;

    } catch (err) {
      console.error(err);
      showNotification("No se pudo cargar la imagen. Intenta con un tamaño menor.", "error");
    } finally {
      setIsUploadingMenu(false);
    }
  };

  const openMediaUrl = (url) => {
    if (!url || typeof url !== 'string') return;
    const targetUrl = getDirectMediaUrl(url);
    const win = window.open('', '_blank');
    if (win) {
      win.document.write(`
        <!DOCTYPE html>
        <html>
          <head><title>Vista previa de la Carta - Keyla CRM</title></head>
          <body style="margin:0; background:#0f172a; display:flex; justify-content:center; align-items:center; min-height:100vh;">
            ${targetUrl.match(/\.(png|jpg|jpeg|webp)($|\?)/i) || targetUrl.startsWith('data:image/') 
              ? `<img src="${targetUrl}" style="max-width:95%; max-height:95vh; border-radius:16px; box-shadow: 0 25px 50px -12px rgba(0,0,0,0.7);" />`
              : `<iframe src="${targetUrl}" style="width:100vw; height:100vh; border:none;"></iframe>`
            }
          </body>
        </html>
      `);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h2 className="text-xl font-extrabold text-seryi-heading">Menú Multimedia 📸</h2>
        <p className="text-xs text-seryi-text">Sube directamente tu archivo PDF o Foto del Menú a la nube, o pega un enlace externo (Google Drive / Canva).</p>
      </div>

      {/* BANNER DESTACADO CON VISTA PREVIA VISUAL DE LA FOTO / DOCUMENTO */}
      {activeBusiness.menu_pdf_url ? (
        <div className="bg-emerald-950/30 border border-emerald-500/40 p-5 rounded-3xl space-y-4 shadow-xl">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-emerald-800/40 pb-4">
            <div className="flex items-center gap-4 overflow-hidden">
              {activeBusiness.menu_pdf_url.startsWith('data:image/') ||
                activeBusiness.menu_pdf_url.includes('lh3.googleusercontent.com') || 
                activeBusiness.menu_pdf_url.match(/\.(png|jpg|jpeg|webp)($|\?)/i) || 
                activeBusiness.menu_pdf_url.includes('menu-files') ? (
                <div className="relative group shrink-0">
                  <img 
                    src={getDirectMediaUrl(activeBusiness.menu_pdf_url)} 
                    alt="Vista previa Menú" 
                    className="w-20 h-20 object-cover rounded-2xl border-2 border-emerald-500/40 shadow-lg bg-seryi-bg-dark"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              ) : (
                <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400 shrink-0 shadow-md">
                  <FileText size={28} />
                </div>
              )}

              <div className="truncate space-y-1">
                <span className="text-[10px] font-black text-emerald-400 uppercase tracking-wider flex items-center gap-1.5">
                  <CheckCircle size={12} /> FOTO / CARTA DIGITAL CARGADA Y ACTIVA EN VIVO
                </span>
                <p className="text-xs text-seryi-heading font-mono truncate max-w-md bg-seryi-bg-dark/80 px-3 py-1.5 rounded-lg border border-seryi-bg-light">
                  {activeBusiness.menu_pdf_url}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              <button 
                type="button"
                onClick={() => openMediaUrl(activeBusiness.menu_pdf_url)}
                className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-seryi-heading rounded-xl text-xs font-bold transition-all shadow-md flex items-center gap-1.5 active:scale-95"
              >
                👀 Ver Archivo Cargado
              </button>
              <button 
                type="button"
                onClick={async () => {
                  updateActiveBusinessField('menu_pdf_url', '');
                  await saveBusinessSettings({ menu_pdf_url: '' });
                  showNotification("Archivo de menú removido.", "info");
                }}
                className="px-3 py-2.5 bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 border border-rose-500/20 rounded-xl text-xs font-bold transition-all"
                title="Eliminar archivo cargado"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>

          <div className="text-[11px] text-emerald-300/90 font-medium flex items-center gap-2">
            <Sparkles size={14} className="text-emerald-400 shrink-0" />
            <span>Keyla IA enviará directamente esta imagen o documento interactivo a los clientes que soliciten el menú en WhatsApp.</span>
          </div>
        </div>
      ) : (
        <div className="bg-amber-950/20 border border-amber-800/40 p-4 rounded-2xl flex items-center gap-3">
          <div className="w-8 h-8 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-400 shrink-0">
            <FileText size={16} />
          </div>
          <p className="text-xs text-amber-300 font-medium">
            ⚠️ Aún no has subido la carta o foto de tu menú. Elige una de las 2 opciones a continuación para activarlo.
          </p>
        </div>
      )}

      <div className="bg-seryi-bg border border-seryi-bg-light p-6 rounded-3xl space-y-6">
        {/* Opción 1: Subida de Foto de la Carta */}
        <div className="space-y-2">
          <span className="text-[11px] font-extrabold uppercase text-seryi-green tracking-wider flex items-center gap-1.5">
            <Upload size={14} /> Opción 1: Subir Foto de la Carta (Recomendado)
          </span>
          
          <div className={`border-2 border-dashed rounded-2xl p-6 text-center transition-all space-y-3 cursor-pointer relative group ${activeBusiness.menu_pdf_url ? 'border-emerald-500/50 bg-emerald-950/10' : 'border-seryi-bg-light hover:border-indigo-500 bg-seryi-bg-dark/60'}`}>
            <input 
              type="file" 
              accept="image/png,image/jpeg,image/webp"
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
              onChange={(e) => {
                if (e.target.files && e.target.files[0]) {
                  handleMenuFileUpload(e.target.files[0]);
                }
              }}
            />
            
            <div className="flex justify-center">
              {activeBusiness.menu_pdf_url ? (
                <div className="w-full flex justify-center">
                  {activeBusiness.menu_pdf_url.match(/\.(jpeg|jpg|gif|png|webp)/i) || 
                  activeBusiness.menu_pdf_url.includes('lh3.googleusercontent.com') || 
                  activeBusiness.menu_pdf_url.startsWith('data:image/') ? (
                    <img 
                      src={activeBusiness.menu_pdf_url} 
                      alt="Menú" 
                      className="max-h-32 object-contain rounded-xl border border-emerald-500/30 group-hover:scale-105 transition-transform shadow-lg" 
                    />
                  ) : (
                    <div className="w-14 h-14 rounded-2xl bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <CheckCircle size={28} />
                    </div>
                  )}
                </div>
              ) : (
                <div className="w-12 h-12 rounded-2xl bg-seryi-green/10 text-seryi-green border border-indigo-500/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <FileText size={24} />
                </div>
              )}
            </div>

            <div className="space-y-1">
              <p className="text-xs font-bold text-seryi-heading">
                {isUploadingMenu 
                  ? "⏳ Optimizando y guardando foto..." 
                  : activeBusiness.menu_pdf_url
                  ? "✅ Foto cargada correctamente. Arrastra otra foto para REEMPLAZARLA"
                  : "Haz clic o arrastra aquí la FOTO de tu Menú"
                }
              </p>
              <p className="text-[10px] text-seryi-text">Imágenes PNG, JPG o WEBP (Máximo 10 MB)</p>
            </div>
            
            {activeBusiness.menu_pdf_url && (
              <div className="pt-2 flex justify-center">
                <button 
                  type="button"
                  onClick={async (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    updateActiveBusinessField('menu_pdf_url', '');
                    await saveBusinessSettings({ menu_pdf_url: '' });
                    showNotification("Archivo de menú removido.", "info");
                  }}
                  className="relative z-20 inline-flex items-center gap-1.5 px-4 py-1.5 bg-rose-500/10 hover:bg-rose-500/30 text-rose-400 border border-rose-500/30 rounded-lg text-[11px] font-bold transition-all"
                >
                  <Trash2 size={14} /> Borrar Imagen/PDF
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Opción 2: Enlace Externo */}
        <div className="space-y-2 pt-4 border-t border-seryi-bg-light/60">
          <span className="text-[11px] font-extrabold uppercase text-seryi-heading tracking-wider flex items-center gap-1.5">
            <Link size={14} /> Opción 2: Pegar Enlace Externo (Drive, Canva, Menú Web)
          </span>
          <div className="flex gap-2">
            <input 
              type="url" 
              placeholder="https://micarta.com/menu"
              className="flex-1 bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-4 py-2.5 text-xs text-seryi-heading focus:outline-none"
              value={tempLinkUrl || activeBusiness.menu_link_url || ''}
              onChange={(e) => setTempLinkUrl(e.target.value)}
            />
            <button 
              type="button"
              onClick={() => {
                const urlToSave = tempLinkUrl;
                updateActiveBusinessField('menu_link_url', urlToSave);
                saveBusinessSettings({ menu_link_url: urlToSave });
                showNotification("Enlace guardado exitosamente.", "success");
              }}
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2.5 px-6 rounded-xl text-xs transition-all active:scale-95 whitespace-nowrap"
            >
              Guardar Enlace
            </button>
          </div>
          <p className="text-[10px] text-seryi-text pl-1">
            Si prefieres no subir un archivo, pega el enlace a tu catálogo web o Drive. La IA enviará este link a tus clientes.
          </p>
        </div>
      </div>
    </div>
  );
}
